import clsx from "clsx";
import React, { Fragment, useState } from "react";
import moment from "moment";

import SpaceDetailsForecastTabSingleGraphView from "./SpaceDetailsForecastTabSingleGraphView";
import Button from "components/common/Button";
import Divider from "components/common/Divider";
import Paper from "components/common/Paper";
import useGetBundleDashboardGWM from "hooks/Dashboard/useGetBundleDashboardGWM";
import SpaceDetailsForecastTabSingleTable from "./SpaceDetailsForecastTabSingleTable";
import MultiChannelSelect from "components/common/MultiChannelSelect";
import useGetSalesChannel from "hooks/Dashboard/useGetSalesChannel";
import Dropdown from "components/common/Dropdown";
import ProductMultiSelectInput from "components/common/ProductMultiSelectInput";
import DateField from "components/common/DateField";
import MenuItem from "components/common/MenuItem";
import { ForecastTabFilter } from "./types/SpaceForecastsTabProps";
import useGetDashboardGWM from "hooks/Dashboard/useGetDashboardGWM";
import SpaceDetailsForecastTabBundleGraphView from "./SpaceDetailsForecastTabBundleGraphView";
import SpaceDetailsForecastTabBundleTable from "./SpaceDetailsForecastTabBundleTable";
import SpaceDetailsForecastTabComponentForecast from "./SpaceDetailsForecastTabComponentForecast";
import { forecastPeriodOptions } from "variables/forecastPeriodOptions";
import useGetNonFinishedGoodsForecast from "hooks/Dashboard/useGetNonFinishedGoodsForecast";

export default function SpaceDetailsForecastTab() {
    const [activeForecastTab, setActiveForecastTab] = useState(0);
    const [refreshNonFinishedGoods, setRefreshNonFinishedGoods] = useState(false);

    const { data: salesChannel, isFetching: salesChannelLoading } = useGetSalesChannel();

    const [filter, setFilter] = useState<ForecastTabFilter>({
        channel: "",
        period: "yearly",
        startDate: "",
        endDate: "",
        componentStartDate: new Date().toISOString(),
        componentEndDate: moment(new Date()).add(6, "months").toISOString(),
        product: "",
        forecastType: "Finished",
        skuType: "Single",
    });

    const {
        data: bundleGwmData,
        isFetching: bundleGwmLoading,
        handleDateRangeChange: bundleHandleDateRangeChange,
        handleChannelChange: bundleHandleChannelChange,
        handleProductChange: bundleHandleProductChange,
        handleStartAndEndDateChange: bundleHandleStartAndEndDateChange,
    } = useGetBundleDashboardGWM(true, undefined, undefined, filter?.product ? false : true);

    const {
        data: singleGwmData,
        isFetching: singleGwmLoading,
        handleDateRangeChange: singleHandleDateRangeChange,
        handleChannelChange: singleHandleChannelChange,
        handleProductChange: singleHandleProductChange,
        handleStartAndEndDateChange: singleHandleStartAndEndDateChange,
    } = useGetDashboardGWM(true, undefined, undefined, filter?.product ? false : true);

    const {
        data: nonFinishedGoodsData,
        isFetching: nonFinishedGoodsLoading,
        setUpdatedPage: nonFinishedGoodsSetUpdatedPage,
        pagination: nonFinishedGoodsPagination,
        setEndDate: nonFinishedGoodsSetEndDate,
    } = useGetNonFinishedGoodsForecast(false, refreshNonFinishedGoods);

    const handleChangeForecastTab = (tab: number) => {
        setActiveForecastTab(tab);
    };

    const handleChannelChange = (value) => {
        setFilter((prev) => ({ ...prev, channel: value }));
        if (filter.skuType === "Single") {
            singleHandleChannelChange(value);
        }
        if (filter.skuType === "Bundle") {
            bundleHandleChannelChange(value);
        }
    };
    const handleProductChange = (name, value) => {
        setFilter((prev) => ({ ...prev, [name]: value }));

        const productValues = value?.split(",")?.filter((item) => item !== "");
        const productIds = productValues
            ?.filter((item) => item !== undefined)
            .map((item) => item?.split("-")[0])
            ?.filter((item) => item !== undefined);

        if (filter.skuType === "Single") {
            singleHandleProductChange(productIds);
        }
        if (filter.skuType === "Bundle") {
            bundleHandleProductChange(productIds);
        }
    };
    const handleDateChange = (name, value) => {
        setFilter((prev) => ({
            ...prev,
            period: value.period,
            startDate: value.startDate,
            endDate: value.endDate,
        }));

        if (filter.skuType === "Single" && value.period === "custom") {
            value.startDate &&
                value.endDate &&
                singleHandleStartAndEndDateChange([value?.startDate, value?.endDate]);
        }
        if (filter.skuType === "Bundle" && value.period === "custom") {
            value.startDate &&
                value.endDate &&
                bundleHandleStartAndEndDateChange([value?.startDate, value?.endDate]);
        }

        if (filter.skuType === "Bundle" && value.period !== "custom") {
            bundleHandleDateRangeChange(value.period);
        }

        if (filter.skuType === "Single" && value.period !== "custom") {
            singleHandleDateRangeChange(value.period);
        }
    };

    const handleComponentDateChange = (name, value) => {
        setFilter((prev) => {
            return {
                ...prev,
                componentStartDate: moment(new Date()).toISOString(),
                componentEndDate: moment(value).toISOString(),
            };
        });
        nonFinishedGoodsSetEndDate(moment(value).toISOString());
    };

    const handleForecastTypeChange = (name, value) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
    };
    const handleSkuTypeChange = (name, value) => {
        setFilter((prev) => ({ ...prev, [name]: value }));
    };

    const contentProps = {
        filter,
        bundleGwmData,
        bundleGwmLoading,
        singleGwmData,
        singleGwmLoading,
        nonFinishedGoodsData,
        nonFinishedGoodsLoading,
        nonFinishedGoodsPagination,
        nonFinishedGoodsSetUpdatedPage,
        setRefreshNonFinishedGoods,
    };

    const forecastTab = [
        {
            title: "Graph View",
            component: (
                <div>
                    {filter.skuType === "Single" && (
                        <SpaceDetailsForecastTabSingleGraphView {...contentProps} />
                    )}
                    {filter.skuType === "Bundle" && (
                        <SpaceDetailsForecastTabBundleGraphView {...contentProps} />
                    )}
                </div>
            ),
        },
        {
            title: "Table View",
            component: (
                <div>
                    {filter.skuType === "Single" && (
                        <SpaceDetailsForecastTabSingleTable filter={filter} />
                    )}
                    {filter.skuType === "Bundle" && (
                        <SpaceDetailsForecastTabBundleTable filter={filter} />
                    )}
                </div>
            ),
        },
    ];

    const forecastTypeOptions = [
        { label: "Finished Goods", value: "Finished" },
        { label: "Components Forecast", value: "Components" },
    ];
    const skuTypeOptions = [
        { label: "Single SKUs", value: "Single" },
        { label: "Bundle SKUs", value: "Bundle" },
    ];

    const getStartAndEndDate = (period: "yearly" | "monthly" | "custom") => {
        if (period === "yearly") {
            return {
                start: moment().startOf("year").format("YYYY-MM-DD"),
                end: moment().endOf("year").format("YYYY-MM-DD"),
            };
        }
        if (period === "monthly") {
            return {
                start: moment().startOf("month").format("YYYY-MM-DD"),
                end: moment().endOf("month").format("YYYY-MM-DD"),
            };
        }
        return {
            start: moment(filter.startDate).format("YYYY-MM-DD"),
            end: moment(filter.endDate).format("YYYY-MM-DD"),
        };
    };

    return (
        <div className="p-[24px]">
            {/* filter */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 w-full">
                <Dropdown
                    handleChange={(name, value) => {
                        handleForecastTypeChange(name, value);
                    }}
                    name="forecastType"
                    value={filter.forecastType}
                    dropdown={
                        <div className="flex items-center justify-between rounded-[6px] py-[6px] px-[10px] gap-2 !border !border-slate-200 max-sm:hidden">
                            <div
                                className={`text-g-75 font-rocGroteskMedium text-[13px] whitespace-nowrap truncate inline-bloc`}
                            >
                                {
                                    forecastTypeOptions.find(
                                        (item) => item.value === filter.forecastType
                                    )?.label
                                }
                            </div>

                            <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                        </div>
                    }
                    dropdownItemsClasses="!px-0 !py-0"
                    dropdownClassName="!w-full !mb-10 !-mt-7"
                    dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                    dropdownOptions={forecastTypeOptions.map((item) => ({
                        label: <MenuItem className="py-[10px] px-4">{item.label}</MenuItem>,
                        value: item.value,
                    }))}
                    className="!w-full"
                />

                {filter.forecastType === "Finished" && (
                    <>
                        {" "}
                        <Dropdown
                            handleChange={(name, value) => {
                                handleSkuTypeChange(name, value);
                            }}
                            value={filter.skuType}
                            name="skuType"
                            dropdown={
                                <div className="flex items-center justify-between rounded-[6px] py-[6px] px-[10px] gap-2 !border !border-slate-200">
                                    <div
                                        className={`text-g-75 font-rocGroteskMedium text-[13px] whitespace-nowrap truncate inline-bloc`}
                                    >
                                        {
                                            skuTypeOptions.find(
                                                (item) => item.value === filter.skuType
                                            )?.label
                                        }
                                    </div>

                                    <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                                </div>
                            }
                            dropdownItemsClasses="!px-0 !py-0 !w-full"
                            dropdownClassName="!w-full !mb-10 !-mt-7"
                            dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-fit !w-auto`}
                            dropdownOptions={skuTypeOptions.map((item) => ({
                                label: <MenuItem className="py-[10px] px-4">{item.label}</MenuItem>,
                                value: item.value,
                            }))}
                            className="!w-full"
                        />
                        <MultiChannelSelect
                            name="channel"
                            salesChannel={salesChannel}
                            value={filter.channel}
                            onChange={(value) => handleChannelChange(value)}
                            isLoading={salesChannelLoading}
                            className="!w-full"
                            endAdornment={
                                <i className="ri-arrow-down-s-line text-lg text-[#142837]"></i>
                            }
                        />
                        <ProductMultiSelectInput
                            name="product"
                            value={filter.product}
                            starredFilter="starred"
                            autoSelectAll
                            handleChange={(name, value) => handleProductChange(name, value)}
                        />
                        <DateField
                            name="dateRange"
                            handleChange={(name, value) => handleDateChange(name, value)}
                        />
                    </>
                )}

                {filter.forecastType === "Components" && (
                    <Dropdown
                        handleChange={(name, value) => {
                            handleComponentDateChange(name, value);
                        }}
                        dropdown={
                            <div className="flex items-center justify-between rounded-[6px] py-[6px] px-[10px] gap-2 !border !border-slate-200">
                                <div
                                    className={`text-g-75 font-rocGroteskMedium text-[11px] xs:text-[13px] inline-block whitespace-nowrap truncate w-fit ${
                                        filter.componentStartDate && "capitalize"
                                    }`}
                                >
                                    {filter.componentStartDate && filter.componentEndDate
                                        ? `${
                                              moment(filter.componentEndDate).diff(
                                                  moment(filter.componentStartDate),
                                                  "months"
                                              ) || 1
                                          } months (${moment(filter.componentStartDate).format(
                                              "MMM YYYY"
                                          )} - ${moment(filter.componentEndDate).format(
                                              "MMM YYYY"
                                          )})`
                                        : "6 months"}
                                </div>
                                <i className="ri-arrow-down-s-line text-xs xs:text-lg text-g-75"></i>
                            </div>
                        }
                        dropdownItemsClasses="!px-0 !py-0"
                        dropdownClassName="!mb-10 !-mt-7"
                        dropdownContainerClasses={`shadow-cardShadow-4 border border-slate-100 right-0 rounded-[6px] !max-h-[200px] !w-[190px] !min-w-min !left-0 !z-[1400]`}
                        dropdownOptions={forecastPeriodOptions}
                    />
                )}
            </div>

            {/* forecast tab */}
            <Paper className="!p-0 mt-[16px]">
                <div className="flex gap-2 justify-between items-center py-[8px] px-[20px]">
                    <p className="text-lg font-rocGroteskMedium">
                        {" "}
                        {filter.forecastType === "Finished"
                            ? `${moment(getStartAndEndDate(filter.period as any).start).format(
                                  "MMMM"
                              )} - ${moment(getStartAndEndDate(filter.period as any).end).format(
                                  "MMMM"
                              )} Forecast`
                            : `${moment(filter.componentStartDate).format("MMMM")} - ${moment(
                                  filter.componentEndDate
                              ).format("MMMM")} Component Forecast`}
                    </p>

                    {filter.forecastType === "Finished" && (
                        <div className="flex gap-2 bg-[#F8FAFC] rounded-[6px] p-1 w-fit">
                            {forecastTab.map((tab, index) => (
                                <Button
                                    btnClassname={clsx(
                                        "!whitespace-nowrap py-[4px] px-[12px]",
                                        activeForecastTab === index
                                            ? "!bg-white rounded-[6px] box-shadow-buttonShadow  text-slate-900"
                                            : "!bg-transparent text-slate-500"
                                    )}
                                    key={index}
                                    onClick={() => handleChangeForecastTab(index)}
                                    btnText={tab.title}
                                />
                            ))}
                        </div>
                    )}
                </div>
                <Divider />
                <Fragment>
                    {filter.forecastType === "Finished" && forecastTab[activeForecastTab].component}

                    {filter.forecastType === "Components" && (
                        <SpaceDetailsForecastTabComponentForecast {...contentProps} />
                    )}
                </Fragment>
            </Paper>
        </div>
    );
}
